import React from "react";
import { icon, IconComponent } from "../../icon-component";
import styles from "./CustomChips.module.css";
import { CustomTooltip } from "../../tooltip";

type ChipColor = "info" | "success" | "warning" | "error" | "neutral";
type ChipSize = "sm" | "md";
type ChipIcon = React.ComponentProps<typeof IconComponent>["icon"];
type IconSize = React.ComponentProps<typeof IconComponent>["size"];

const iconSizes: Record<ChipSize, IconSize> = {
	sm: "xs",
	md: "sm",
};

const endIconSizes: Record<ChipSize, IconSize> = {
	sm: "2xs",
	md: "xs",
};

interface CustomChipsProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "color" | "onClick" | "onClose"> {
	readonly label?: string;
	readonly startIcon?: ChipIcon | null;
	readonly endIcon?: ChipIcon;
	readonly isActive?: boolean;
	readonly showTooltip?: boolean;
	readonly color?: ChipColor;
	readonly size?: ChipSize;
	readonly className?: string;
	readonly closable?: boolean;
	readonly onClose?: ((event: React.MouseEvent<HTMLButtonElement>) => void) | null;
	readonly onClick?: (() => void) | null;
}

const CustomChips = React.forwardRef<HTMLDivElement, CustomChipsProps>(
	(
		{
			label = "",
			startIcon = null,
			endIcon = icon.faTimes,
			color = "info",
			size = "md",
			className = "",
			closable = false,
			isActive = false,
			showTooltip = true,
			onClose = null,
			onClick = null,
			...rest
		}: CustomChipsProps,
		ref
	) => {
		const isClickable = !!onClick;

		const handleClose = (event: React.MouseEvent<HTMLButtonElement>) => {
			event.stopPropagation();
			onClose?.(event);
		};

		const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
			if (event.target === event.currentTarget && (event.key === "Enter" || event.key === " ")) {
				event.preventDefault();
				onClick?.();
			}
		};

		const clickableProps = isClickable
			? { role: "button", tabIndex: 0, onClick: () => onClick?.(), onKeyDown: handleKeyDown }
			: {};

		const chip = (
			<div
				ref={ref}
				{...rest}
				{...clickableProps}
				className={`${styles.chip} ${className}`}
				data-clickable={isClickable}
				data-color={color}
				data-is-active={isActive}
				data-size={size}
			>
				{startIcon && (
					<IconComponent
						className={styles.startIcon}
						color="currentColor"
						icon={startIcon}
						size={iconSizes[size]}
					/>
				)}
				<span data-truncate className={styles.label}>
					{label}
				</span>
				{closable && (
					<button className={styles.endIconBtn} type="button" onClick={handleClose}>
						<IconComponent
							className={styles.endIcon}
							color="currentColor"
							icon={endIcon}
							size={endIconSizes[size]}
						/>
					</button>
				)}
			</div>
		);

		if (!showTooltip) {
			return chip;
		}

		return (
			<CustomTooltip showWhenOverflow title={label}>
				{chip}
			</CustomTooltip>
		);
	}
);

export default CustomChips;
